/**
 * Free NGO signup page — public, no auth required.
 * Creates a PENDING adoption_agencies row via POST /api/adoption-agencies/public-signup.
 * Admin approves in /ops → the row goes live at /ngo/{slug}.
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Heart, Loader2, CheckCircle2 } from 'lucide-react';
import SEO from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

export default function NgoJoinPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {slug, message}
  const [form, setForm] = useState({
    name: '',
    email: '',
    mission: '',
    website: '',
    city: '',
    state: '',
    representative_name: '',
    representative_title: '',
    ein_number: '',
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.mission.length < 20) {
      toast.error('Mission must be at least 20 characters');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/adoption-agencies/public-signup`, form);
      setSubmitted(res.data);
      toast.success('Application received!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex items-center justify-center p-4">
        <SEO title="Application received — All American Dog Club" description="Your NGO listing is queued for review." />
        <Card className="max-w-lg w-full" data-testid="ngo-signup-success">
          <CardContent className="text-center p-8">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-bold mb-2">Thanks — you&apos;re in the queue</h1>
            <p className="text-muted-foreground mb-4">{submitted.message}</p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-left text-sm mb-6">
              <div className="font-medium mb-1">Your future promo page:</div>
              <div className="text-orange-700 font-mono break-all">
                {window.location.origin}/ngo/{submitted.slug}
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                Goes live once we&apos;ve reviewed and approved your listing. We&apos;ll email {form.email} when it&apos;s public.
              </div>
            </div>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={() => navigate('/')} data-testid="ngo-signup-back-home">
                Back to home
              </Button>
              <Button
                onClick={() => {
                  setSubmitted(null);
                  setForm({ name: '', email: '', mission: '', website: '', city: '', state: '', representative_name: '', representative_title: '', ein_number: '' });
                }}
                data-testid="ngo-signup-submit-another"
              >
                Submit another
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white py-12 px-4">
      <SEO
        title="Free NGO Listing — All American Dog Club"
        description="Rescues and shelters — get a free promo page on the All American Dog Club platform. No fees, no strings."
      />
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2">
            Free promo page for your rescue
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            We list every dog rescue and shelter that applies — for free, always. Get a shareable page with your mission, logo, and links.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Tell us about your organization</CardTitle>
            <CardDescription>
              Only <strong>name</strong>, <strong>email</strong>, and <strong>mission</strong> are required — everything else can be filled in later.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Organization name *</Label>
                <Input
                  id="name"
                  data-testid="ngo-signup-name"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  required
                  minLength={2}
                  maxLength={200}
                  placeholder="Golden Heart Rescue"
                />
              </div>

              <div>
                <Label htmlFor="email">Contact email *</Label>
                <Input
                  id="email"
                  data-testid="ngo-signup-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  required
                  placeholder="contact@yourrescue.org"
                />
              </div>

              <div>
                <Label htmlFor="mission">Mission * <span className="text-xs text-muted-foreground">(20-500 chars)</span></Label>
                <Textarea
                  id="mission"
                  data-testid="ngo-signup-mission"
                  value={form.mission}
                  onChange={(e) => set('mission', e.target.value)}
                  required
                  minLength={20}
                  maxLength={500}
                  rows={4}
                  placeholder="We rescue senior dogs from high-kill shelters and place them in loving foster homes across the Midwest."
                />
                <div className="text-xs text-muted-foreground mt-1">{form.mission.length}/500</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input id="city" data-testid="ngo-signup-city" value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="Chicago" />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input id="state" data-testid="ngo-signup-state" value={form.state} onChange={(e) => set('state', e.target.value)} maxLength={2} placeholder="IL" />
                </div>
              </div>

              <div>
                <Label htmlFor="website">Website</Label>
                <Input id="website" data-testid="ngo-signup-website" type="url" value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://yourrescue.org" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="rep_name">Your name</Label>
                  <Input id="rep_name" data-testid="ngo-signup-rep-name" value={form.representative_name} onChange={(e) => set('representative_name', e.target.value)} placeholder="Jane Doe" />
                </div>
                <div>
                  <Label htmlFor="rep_title">Your role</Label>
                  <Input id="rep_title" data-testid="ngo-signup-rep-title" value={form.representative_title} onChange={(e) => set('representative_title', e.target.value)} placeholder="Executive Director" />
                </div>
              </div>

              <div>
                <Label htmlFor="ein">EIN / Tax ID <span className="text-xs text-muted-foreground">(optional but speeds review)</span></Label>
                <Input id="ein" data-testid="ngo-signup-ein" value={form.ein_number} onChange={(e) => set('ein_number', e.target.value)} placeholder="12-3456789" />
              </div>

              <div className="pt-2">
                <Button
                  type="submit"
                  className="w-full btn-primary"
                  disabled={submitting}
                  data-testid="ngo-signup-submit"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
                  ) : (
                    'Submit for review'
                  )}
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-3">
                  Already have a listing? <Link to="/contact" className="text-primary hover:underline">Contact us to claim it</Link>.
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
